/**
 * De onde a família veio (30/09/2026, checklist "piloto automático" C.4): a
 * primeira venda real entrou com `src` vazio, e o carrossel de anúncio do camp
 * sai hoje.
 *
 * Três caminhos, na ordem em que a inscrição confia:
 *   1. o corpo do POST: a página de inscrição lê `src` e `utm_*` da própria URL;
 *   2. o cookie `camp_origem`: o proxy grava na PRIMEIRA visita com parâmetro e
 *      não sobrescreve depois (primeiro toque), então quem volta dias depois,
 *      sem parâmetro nenhum, ainda credita a origem;
 *   3. nada: fica nulo, sem erro.
 *
 * O mesmo desenho do código de treinador (`treinador.ts`): cookie de 30 dias,
 * gravado no proxy, que roda também nas páginas estáticas do camp.
 */

export const COOKIE_DA_ORIGEM = "camp_origem";
export const DIAS_DA_ORIGEM = 30;

export const CHAVES_DA_ORIGEM = ["src", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
export type ChaveDaOrigem = (typeof CHAVES_DA_ORIGEM)[number];
export type Origem = Record<ChaveDaOrigem, string | null>;

/** Minúsculo, sem espaço nas pontas, até 80 caracteres. Vazio vira null. */
export function limparValorDaOrigem(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim().toLowerCase().slice(0, 80);
  return t || null;
}

export function origemVazia(): Origem {
  return { src: null, utm_source: null, utm_medium: null, utm_campaign: null, utm_content: null, utm_term: null };
}

/** A origem que veio num objeto qualquer (corpo do POST, props). */
export function origemDoObjeto(o: Record<string, unknown> | null | undefined): Origem {
  const r = origemVazia();
  for (const k of CHAVES_DA_ORIGEM) r[k] = limparValorDaOrigem(o?.[k]);
  return r;
}

/** A origem dos parâmetros da URL, ou null se não veio nenhum. */
export function origemDosParams(params: URLSearchParams): Origem | null {
  const r = origemVazia();
  let alguma = false;
  for (const k of CHAVES_DA_ORIGEM) {
    r[k] = limparValorDaOrigem(params.get(k));
    if (r[k]) alguma = true;
  }
  return alguma ? r : null;
}

/** O valor do cookie: só as chaves preenchidas, no formato de query string. */
export function codificarOrigem(o: Origem): string {
  const p = new URLSearchParams();
  for (const k of CHAVES_DA_ORIGEM) if (o[k]) p.set(k, o[k]!);
  return p.toString();
}

/** Lê o cookie `camp_origem` do cabeçalho Cookie. Sem cookie, tudo nulo. */
export function origemDoCookie(cabecalhoCookie: string | null): Origem {
  const bruto = cabecalhoCookie?.match(new RegExp(`(?:^|;\\s*)${COOKIE_DA_ORIGEM}=([^;]+)`))?.[1];
  if (!bruto) return origemVazia();
  let valor = bruto;
  try {
    valor = decodeURIComponent(bruto);
  } catch {
    return origemVazia();
  }
  return origemDosParams(new URLSearchParams(valor)) ?? origemVazia();
}

/** Campo a campo: o que veio no corpo, senão o que o cookie guardou. */
export function juntarOrigem(doCorpo: Origem, doCookie: Origem): Origem {
  const r = origemVazia();
  for (const k of CHAVES_DA_ORIGEM) r[k] = doCorpo[k] ?? doCookie[k];
  return r;
}
